import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import bgImage from "../img/background_forest-g7.jpg";
import avgConsultingWebp from "../img/AVGConsulting-ezgif.com-video-to-webp-converter.webp";

const defaultForm = {
  location: "",
  farmName: "",
  cropType: "",
  surfaceHa: "",
  stage: "",
  budget: "",
  needs: [],
  fullName: "",
  email: "",
  phone: "",
  contactPreference: "",
  message: ""
};

const needsOptions = [
  "Structuration",
  "Planification",
  "Suivi terrain",
  "Collecte de données",
  "Analyse des performances",
  "Pilotage stratégique",
  "Transparence investisseurs/diaspora",
  "Accès au financement"
];

const DiagnosticSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const steps = useMemo(() => ["Exploitation", "Projet", "Contact"], []);
  const avgText = useMemo(
    () =>
      "AVG Consulting accompagne les producteurs agricoles, les porteurs de projets, les investisseurs et la diaspora dans l’organisation, le suivi et le pilotage de leurs projets agricoles.\n\nNotre mission est d’aider à structurer les exploitations grâce à l’organisation des activités, la planification des opérations, le suivi terrain, la collecte et l’analyse des données agricoles, et le pilotage stratégique.",
    []
  );

  const toggleNeed = (value) => {
    setForm((prev) => {
      const exists = prev.needs.includes(value);
      return { ...prev, needs: exists ? prev.needs.filter((v) => v !== value) : [...prev.needs, value] };
    });
  };

  const canNext = () => {
    if (activeStep === 0) return Boolean(form.farmName) && Boolean(form.cropType);
    if (activeStep === 1) return Boolean(form.location) && Boolean(form.stage);
    if (activeStep === 2) return Boolean(form.fullName) && Boolean(form.email);
    return true;
  };

  const next = () => {
    if (!canNext()) {
      setSnackbar({ open: true, severity: "error", message: "Merci de compléter les champs obligatoires de cette étape." });
      return;
    }
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const back = () => setActiveStep((s) => Math.max(s - 1, 0));

  const toEmailPayload = () => {
    const lines = [
      `Localisation : ${form.location || "Non précisée"}`,
      `Nom de l’exploitation : ${form.farmName || "Non précisé"}`,
      `Type de culture : ${form.cropType || "Non précisé"}`,
      `Surface (ha) : ${form.surfaceHa || "Non précisée"}`,
      `Stade du projet : ${form.stage || "Non précisé"}`,
      `Budget estimé : ${form.budget || "Non précisé"}`,
      `Besoins : ${form.needs.length ? form.needs.join(", ") : "Non précisés"}`,
      "",
      `Nom : ${form.fullName || "Non précisé"}`,
      `Email : ${form.email || "Non précisé"}`,
      `Téléphone : ${form.phone || "Non précisé"}`,
      `Préférence de contact : ${form.contactPreference || "Non précisée"}`,
      "",
      form.message || ""
    ];
    return lines.join("\n");
  };

  const submit = async () => {
    if (!canNext()) {
      setSnackbar({ open: true, severity: "error", message: "Merci de compléter les champs obligatoires." });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        createdAt: serverTimestamp(),
        source: "site",
        status: "new"
      };
      await addDoc(collection(db, "diagnostic_requests"), payload);
      setSnackbar({ open: true, severity: "success", message: "Demande envoyée. Nous revenons vers vous rapidement." });
      setForm(defaultForm);
      setActiveStep(0);
    } catch (e) {
      const subject = "AVG Consulting - Réservation diagnostic";
      const body = toEmailPayload();
      const mailto = `mailto:agrivision.ga@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Envoi via email (ouverture de votre messagerie)."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box id="avg-consulting" sx={{ py: { xs: 7, md: 10 } }}>
      <Box id="diagnostic" />
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, color: "white", textTransform: "uppercase", letterSpacing: 1, mb: 1 }}
          >
            Réserver un diagnostic
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.85)", maxWidth: 980, mx: "auto", lineHeight: 1.8 }}>
            Un diagnostic structuré pour comprendre votre situation, clarifier vos objectifs et définir les priorités d’organisation,
            de suivi et de pilotage.
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: { xs: 3, md: 4 },
            boxShadow: "0 18px 55px rgba(0,0,0,0.22)",
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.18)"
          }}
        >
          <Grid container>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  height: "100%",
                  minHeight: { xs: "auto", md: 560 },
                  p: { xs: 3, md: 3.5 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: { xs: 3, md: 0 },
                  backgroundImage: `linear-gradient(rgba(1,69,39,0.80), rgba(1,69,39,0.80)), url(${bgImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "white", mb: 1 }}>
                    AVG Consulting
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.88)",
                      lineHeight: 1.8,
                      whiteSpace: "pre-line",
                      fontSize: { xs: "1rem", md: "0.95rem" }
                    }}
                  >
                    {avgText}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: { xs: 0, md: 3 },
                    height: { xs: 220, sm: 260, md: 240 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Box
                    component="img"
                    src={avgConsultingWebp}
                    alt="Scenario AVG Consulting"
                    loading="eager"
                    sx={{
                      width: "100%",
                      maxWidth: { xs: 420, md: 360 },
                      height: "100%",
                      objectFit: "contain",
                      filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.25))",
                      opacity: 0.98,
                      borderRadius: 2
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", letterSpacing: 1, fontWeight: 800 }}>
                      SECTEUR
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white", fontWeight: 700 }}>
                      Agriculture
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", letterSpacing: 1, fontWeight: 800 }}>
                      APPROCHE
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white", fontWeight: 700 }}>
                      Mesure & pilotage
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{ bgcolor: "white" }}>
                <Box sx={{ px: { xs: 2.5, md: 3.5 }, pt: { xs: 2.5, md: 3.5 }, pb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 900,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          lineHeight: 1.2
                        }}
                      >
                        Réserver un diagnostic
                      </Typography>
                      <Box sx={{ width: 56, height: 3, bgcolor: "primary.main", borderRadius: 99, mt: 1.5 }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, letterSpacing: 0.8 }}>
                      ÉTAPE {activeStep + 1} SUR {steps.length}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
                    {activeStep === 0
                      ? "Identification de l’exploitation"
                      : activeStep === 1
                        ? "Informations projet & besoins"
                        : "Vos coordonnées"}
                  </Typography>
                </Box>

                <CardContent sx={{ px: { xs: 2.5, md: 3.5 }, pt: 0, pb: { xs: 2.5, md: 3.5 } }}>
                  {activeStep === 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField
                        label="Nom de l’exploitation"
                        value={form.farmName}
                        onChange={(e) => setForm((p) => ({ ...p, farmName: e.target.value }))}
                        fullWidth
                        required
                      />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Type de culture"
                            value={form.cropType}
                            onChange={(e) => setForm((p) => ({ ...p, cropType: e.target.value }))}
                            fullWidth
                            required
                          >
                            <MenuItem value="">Choisir</MenuItem>
                            <MenuItem value="manioc">Manioc</MenuItem>
                            <MenuItem value="maraichage">Maraîchage</MenuItem>
                            <MenuItem value="plantain">Plantain</MenuItem>
                            <MenuItem value="elevage">Élevage</MenuItem>
                            <MenuItem value="autre">Autre</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Superficie (ha)"
                            value={form.surfaceHa}
                            onChange={(e) => setForm((p) => ({ ...p, surfaceHa: e.target.value }))}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {activeStep === 1 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField
                        label="Localisation (pays / province / ville)"
                        value={form.location}
                        onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                        fullWidth
                        required
                        placeholder="Ex: Gabon, Estuaire, Libreville"
                      />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Stade du projet"
                            value={form.stage}
                            onChange={(e) => setForm((p) => ({ ...p, stage: e.target.value }))}
                            fullWidth
                            required
                          >
                            <MenuItem value="">Choisir</MenuItem>
                            <MenuItem value="idee">Idée / réflexion</MenuItem>
                            <MenuItem value="lancement">Lancement</MenuItem>
                            <MenuItem value="production">En production</MenuItem>
                            <MenuItem value="developpement">Développement / expansion</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Budget estimé (optionnel)"
                            value={form.budget}
                            onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))}
                            fullWidth
                            placeholder="Ex: 2 000 000 FCFA"
                          />
                        </Grid>
                      </Grid>

                      <Divider />

                      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "text.primary" }}>
                        Besoins prioritaires
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {needsOptions.map((opt) => (
                          <Chip
                            key={opt}
                            label={opt}
                            clickable
                            onClick={() => toggleNeed(opt)}
                            color={form.needs.includes(opt) ? "primary" : "default"}
                            variant={form.needs.includes(opt) ? "filled" : "outlined"}
                          />
                        ))}
                      </Box>

                      <TextField
                        label="Message (optionnel)"
                        value={form.message}
                        onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="Décrivez votre projet, vos contraintes, vos objectifs…"
                      />
                    </Box>
                  )}

                  {activeStep === 2 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Nom et prénom"
                            value={form.fullName}
                            onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                            fullWidth
                            required
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Téléphone (optionnel)"
                            value={form.phone}
                            onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Préférence de contact (optionnel)"
                            value={form.contactPreference}
                            onChange={(e) => setForm((p) => ({ ...p, contactPreference: e.target.value }))}
                            fullWidth
                          >
                            <MenuItem value="">Choisir</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="telephone">Téléphone</MenuItem>
                            <MenuItem value="whatsapp">WhatsApp</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
                    <Button disabled={activeStep === 0 || submitting} onClick={back} sx={{ textTransform: "none" }}>
                      Retour
                    </Button>

                    {activeStep < steps.length - 1 ? (
                      <Button
                        variant="contained"
                        disabled={!canNext() || submitting}
                        onClick={next}
                        sx={{ textTransform: "none", fontWeight: 800, px: 3 }}
                      >
                        Continuer
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        disabled={!canNext() || submitting}
                        onClick={submit}
                        sx={{ textTransform: "none", fontWeight: 800, px: 3 }}
                      >
                        Envoyer
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DiagnosticSection;
